import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebaseconfig';
import { useAuth } from '../../hooks/useAuth';
import './CalccafeMain.css';

// Importe os ícones específicos do café
import logo from "../../assets/calccafe.png";
import manualIcon from "../../assets/manual.png"; // Pode ser o mesmo ícone
import producaoIcon from "../../assets/producao.png"; // Reutilizando ícones por enquanto
import capitalIcon from "../../assets/capital.png";
import custosIcon from "../../assets/custos.png";
import receitaIcon from "../../assets/receita.png";
import relatorioIcon from "../../assets/relatorio.png";

const toDate = (timestamp) => {
  if (!timestamp) return null;
  if (typeof timestamp.toDate === "function") {
    return timestamp.toDate();
  }
  const date = new Date(timestamp);
  return isNaN(date) ? null : date;
};

const CalcCafeMain = () => {
  const { user, userData, loading } = useAuth();
  const navigate = useNavigate();

  const [modalVisible, setModalVisible] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const APP_TAG = 'calccafe'; // <-- MUDANÇA PRINCIPAL
  const lastSyncAttemptRef = useRef(0);

  // Função para forçar a sincronização dos dados da assinatura
  const syncSubscription = async () => {
    if (!user || isSyncing) return;
    const now = Date.now();
    if (now - lastSyncAttemptRef.current < 10000) return;
    
    setIsSyncing(true);
    lastSyncAttemptRef.current = now;
    
    try {
      const token = await user.getIdToken(true);
      await fetch("https://backendcustosagro-e51a.onrender.com/sync-subscription", {
        method: "POST",
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ uid: user.uid }),
      });
    } catch (error) {
      console.error("Erro ao sincronizar assinatura:", error);
    } finally {
      setIsSyncing(false);
    }
  };

  useEffect(() => {
    if (user) {
        syncSubscription();
    }
  }, [user]);

  // Verifica se o usuário tem acesso a este app
  useEffect(() => {
    if (!loading && userData && !userData[APP_TAG]) {
      alert("Sua assinatura para o CalcCafé não está ativa ou expirou.");
      navigate("/planos/calccafe"); // Redireciona para os planos corretos
    }
  }, [userData, loading, navigate]);

  const subscriptionData = useMemo(() => 
    userData?.subscriptions?.[APP_TAG] || {}, 
  [userData]);

  const dataTermino = useMemo(() => {
    const periodEnd = toDate(subscriptionData.currentPeriodEnd);
    return periodEnd;
  }, [subscriptionData]);
  
  const dataFormatada = useMemo(() => 
    dataTermino ? dataTermino.toLocaleDateString("pt-BR") : "N/A",
  [dataTermino]);
  
  const handleLogout = async () => {
    await signOut(auth);
    navigate("/login");
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm("Você tem certeza que deseja cancelar sua assinatura do CalcCafé?")) return;
    setCancelLoading(true);
    try {
      const token = await user.getIdToken(true);
      await fetch("https://backendcustosagro-e51a.onrender.com/cancel-subscription", {
        method: "POST",
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ uid: user.uid, app: APP_TAG }),
      });
      await syncSubscription();
      alert("Sucesso! Sua assinatura foi marcada para cancelamento.");
      setModalVisible(false);
    } catch (error) {
      alert(`Erro: ${error.message}`);
    } finally {
      setCancelLoading(false);
    }
  };

  const handleDeleteAccount = async () => { /* ...lógica idêntica... */ };

  if (loading) {
    return <div className="ccm-loading-container">Carregando...</div>;
  }
  
  const willCancelField = `willCancelAtPeriodEnd${APP_TAG.charAt(0).toUpperCase() + APP_TAG.slice(1)}`;
  const willCancel = userData?.[willCancelField] || false;

  return (
    <div className="ccm-container">
      <button className="ccm-settings-button" onClick={() => setModalVisible(true)}>
        Configurações {isSyncing && "🔄"}
      </button>

      <header className="ccm-header-content">
        <img src={logo} alt="CalcCafé Logo" className="ccm-logo" />
        <h1 className="ccm-title">CalcCafé</h1>
        <p className="ccm-subtitle">
          Gerencie custos, rentabilidade e simule cenários para sua lavoura de café.
        </p>
      </header>

      <div className="ccm-grid">
        <button className="ccm-card" onClick={() => navigate('InstrucoesCafe')}>
          <img src={manualIcon} alt="Ícone de manual" className="ccm-icon" />
          <span>INSTRUÇÕES</span>
        </button>
        <button className="ccm-card" onClick={() => navigate('/calccafe/app/producao')}>
          <img src={producaoIcon} alt="Ícone de produção" className="ccm-icon" />
          <span>PRODUÇÃO E PRODUTIVIDADE</span>
        </button>
        <button className="ccm-card" onClick={() => navigate("/calccafe/app/capitalCafe")}>
          <img src={capitalIcon} alt="Ícone de capital" className="ccm-icon" />
          <span>CUSTO DO CAPITAL E DEPRECIAÇÃO</span>
        </button>
        <button className="ccm-card" onClick={() => navigate("/calccafe/app/custosCafe")}>
          <img src={custosIcon} alt="Ícone de custos" className="ccm-icon" />
          <span>CUSTO OPERACIONAL E TOTAL</span>
        </button>
        <button className="ccm-card" onClick={() => navigate("/calccafe/app/receitaCafe")}>
          <img src={receitaIcon} alt="Ícone de receita" className="ccm-icon" />
          <span>RECEITA E RENTABILIDADE</span>
        </button>
        <button className="ccm-card" onClick={() => navigate("/calccafe/app/relatorioCafe")}>
          <img src={relatorioIcon} alt="Ícone de relatório" className="ccm-icon" />
          <span>RELATÓRIO FINAL</span>
        </button>
      </div>

      {modalVisible && (
        <div className="ccm-modal-backdrop">
          <div className="ccm-modal-content">
            <h2>Configurações</h2>
            <p><strong>Email:</strong> {userData?.customerEmail || 'N/A'}</p>
            <p><strong>Plano:</strong> {subscriptionData.plan || 'Nenhum'}</p>
            <p><strong>Status:</strong> {subscriptionData.status === 'trialing' ? 'Período de teste' : (subscriptionData.status || 'N/A')}</p>
            {dataFormatada !== "N/A" && <p><strong>{willCancel ? "Expira em:" : "Próxima cobrança:"}</strong> {dataFormatada}</p>}
            <div className="ccm-modal-actions">
              {!willCancel && subscriptionData.status !== 'canceled' ? (
                <button className="ccm-modal-button cancel" onClick={handleCancelSubscription} disabled={cancelLoading}>
                  {cancelLoading ? 'Cancelando...' : 'Cancelar Assinatura'}
                </button>
              ) : (
                <p className="ccm-cancelled-text">Sua assinatura já foi cancelada e não será renovada.</p>
              )}
              <button className="ccm-modal-button logout" onClick={handleLogout}>Sair</button>
            </div>
            <button className="ccm-modal-button close" onClick={() => setModalVisible(false)}>Fechar</button>
          </div>
        </div>
      )}

      <footer className="ccm-footer">
        <p>Copyright © 2024. Todos os Direitos Reservados.</p>
      </footer>
    </div>
  );
};

export default CalcCafeMain;
